/**
 * Several models, one point, one table.
 *
 * This is what sits behind "tap a spot and show me the wind per hour per day",
 * and behind comparing models side by side. One position, a chosen set of models,
 * a chosen set of variables, an hourly series each.
 *
 * ONE REQUEST PER MODEL, deliberately, rather than one request naming them all.
 * Two reasons and both matter. A model that is late, down, or has no coverage
 * where the boat is fails on its own and the rest of the table still fills, which
 * is exactly the behaviour you want from something whose whole job is showing you
 * more than one opinion. And each response comes back in the plain shape the rest
 * of this code already parses, rather than the suffixed shape a multi-model
 * request returns, so there is nothing to guess about. The requests are small and
 * they go in parallel, so the cost is one round trip, not six.
 */
import type { WeatherModel } from './models';

type FetchLike = (
  url: string,
) => Promise<{ ok: boolean; status: number; json: () => Promise<unknown> }>;

/** The things a skipper can ask to see, and what Open-Meteo calls them. */
export interface WeatherVariable {
  id: string;
  /** Column heading. */
  name: string;
  /** The Open-Meteo hourly variable name. */
  api: string;
  unit: string;
  /** True for anything in degrees, so the consensus uses circular maths. */
  isDirection?: boolean;
  /** Decimal places when printed. */
  dp: number;
}

export const WEATHER_VARIABLES: WeatherVariable[] = [
  { id: 'wind', name: 'Wind', api: 'wind_speed_10m', unit: 'kn', dp: 0 },
  { id: 'gust', name: 'Gust', api: 'wind_gusts_10m', unit: 'kn', dp: 0 },
  { id: 'dir', name: 'From', api: 'wind_direction_10m', unit: '°', isDirection: true, dp: 0 },
  { id: 'rain', name: 'Rain', api: 'precipitation', unit: 'mm', dp: 1 },
  { id: 'pressure', name: 'Pressure', api: 'pressure_msl', unit: 'hPa', dp: 0 },
  { id: 'cloud', name: 'Cloud', api: 'cloud_cover', unit: '%', dp: 0 },
  { id: 'temp', name: 'Air', api: 'temperature_2m', unit: '°C', dp: 0 },
  { id: 'visibility', name: 'Visibility', api: 'visibility', unit: 'm', dp: 0 },
];

export function variableById(id: string): WeatherVariable | null {
  return WEATHER_VARIABLES.find((v) => v.id === id) ?? null;
}

/** One model's answer for one point. `error` set means it did not give us one. */
export interface ModelSeries {
  modelId: string;
  timesMs: number[];
  /** Keyed by variable id. Null entries are hours the model did not answer for. */
  values: Record<string, (number | null)[]>;
  error?: string;
}

export interface ForecastTable {
  position: [number, number];
  /** The hour grid, taken from the first model that answered. */
  timesMs: number[];
  series: ModelSeries[];
}

/** Build the request for one model at one point. */
export function buildModelUrl(
  position: [number, number],
  modelId: string,
  vars: WeatherVariable[],
  days = 7,
): string {
  const hourly = vars.map((v) => v.api).join(',');
  return (
    'https://api.open-meteo.com/v1/forecast' +
    `?latitude=${position[1].toFixed(4)}&longitude=${position[0].toFixed(4)}` +
    `&hourly=${hourly}` +
    '&wind_speed_unit=kn&timezone=UTC' +
    `&forecast_days=${Math.max(1, Math.min(16, Math.round(days)))}` +
    `&models=${modelId}`
  );
}

interface HourlyResponse {
  hourly?: Record<string, unknown>;
}

/**
 * Turn one model's response into a series.
 *
 * Nulls stay null the whole way through. Open-Meteo returns null for an hour or a
 * variable a model does not carry, and Number(null) is 0, which on this screen
 * would read as dead calm, no rain and a pressure of zero. All three would be
 * lies told confidently.
 */
export function parseModelSeries(
  json: unknown,
  modelId: string,
  vars: WeatherVariable[],
): ModelSeries {
  const h = (json as HourlyResponse)?.hourly ?? {};
  const times = Array.isArray(h.time) ? (h.time as string[]) : [];
  const values: Record<string, (number | null)[]> = {};
  for (const v of vars) {
    const raw = h[v.api];
    values[v.id] = Array.isArray(raw)
      ? raw.map((x) => {
          if (x == null) return null;
          const n = Number(x);
          return Number.isFinite(n) ? n : null;
        })
      : [];
  }
  return { modelId, timesMs: times.map((t) => Date.parse(`${t}Z`)), values };
}

/**
 * Fetch every model for one point, in parallel, each isolated from the others.
 *
 * A model that fails comes back as a series with an error on it rather than
 * missing, so the table can show a column that says why it is empty instead of
 * quietly having one fewer opinion in it than the skipper thinks.
 */
export async function fetchForecastTable(
  position: [number, number],
  models: WeatherModel[],
  vars: WeatherVariable[] = WEATHER_VARIABLES,
  fetchImpl: FetchLike = fetch as unknown as FetchLike,
): Promise<ForecastTable> {
  const series = await Promise.all(
    models.map(async (m): Promise<ModelSeries> => {
      try {
        const r = await fetchImpl(buildModelUrl(position, m.id, vars, m.days));
        if (!r.ok) {
          return { modelId: m.id, timesMs: [], values: {}, error: `HTTP ${r.status}` };
        }
        return parseModelSeries(await r.json(), m.id, vars);
      } catch (e) {
        return {
          modelId: m.id,
          timesMs: [],
          values: {},
          error: e instanceof Error ? e.message : 'fetch failed',
        };
      }
    }),
  );
  // The hour grid is whichever model answered first with one. They all publish
  // on the hour in UTC, so the grids line up; only their LENGTHS differ, which is
  // why a short-range model simply runs out partway along the table.
  const timesMs = series.find((s) => s.timesMs.length)?.timesMs ?? [];
  return { position, timesMs, series };
}

/**
 * Every model's value for one variable at one time, ready for the consensus.
 *
 * Matched by TIMESTAMP rather than by index, because the models have different
 * run lengths and a short one must not have its hour 12 lined up against another
 * model's hour 12 when the two tables start at different hours.
 */
export function valuesAt(
  table: ForecastTable,
  variableId: string,
  ms: number,
): { modelId: string; value: number | null }[] {
  return table.series.map((s) => {
    const i = s.timesMs.indexOf(ms);
    const v = i < 0 ? null : (s.values[variableId]?.[i] ?? null);
    return { modelId: s.modelId, value: v };
  });
}

/** The distinct UTC days the table covers, as midnight timestamps. */
export function daysIn(table: ForecastTable): number[] {
  const out: number[] = [];
  let last = -1;
  for (const ms of table.timesMs) {
    const day = Math.floor(ms / 86_400_000) * 86_400_000;
    if (day !== last) {
      out.push(day);
      last = day;
    }
  }
  return out;
}
